import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import WorkOrderDetail from './WorkOrderDetail';
import MaintenanceTaskDetail from './MaintenanceTaskDetail';
import { useLanguage } from '@/contexts/LanguageContext';
import { 
  Calendar,
  ChevronLeft,
  ChevronRight,
  Clock,
  Settings,
  Wrench,
  Filter
} from 'lucide-react';

function CalendarView({ user }) {
  const { t } = useLanguage();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [view, setView] = useState('month');
  const [selectedTask, setSelectedTask] = useState(null);
  const [workOrders, setWorkOrders] = useState([]);
  const [maintenanceTasks, setMaintenanceTasks] = useState({ daily: [], weekly: [], monthly: [], yearly: [] });
  const [completedInstances, setCompletedInstances] = useState([]); // Track completed task instances
  const [loading, setLoading] = useState(true);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  // Load completed task instances from history
  useEffect(() => {
    const loadCompletedInstances = async () => {
      try {
        // Load both maintenance and repair history
        const [maintenanceRes, repairRes] = await Promise.all([
          fetch(`${API}/maintenance-history`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          }),
          fetch(`${API}/repair-history`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          })
        ]);
        
        if (maintenanceRes.ok && repairRes.ok) {
          const maintenanceHistory = await maintenanceRes.json();
          const repairHistory = await repairRes.json();
          
          // Create a map of work_order_id + scheduled_date for quick lookup
          const completedMap = {};
          
          [...maintenanceHistory, ...repairHistory].forEach(item => {
            const key = `${item.work_order_id}_${item.scheduled_date}`;
            completedMap[key] = true;
            console.log('History completion key:', key, 'for task:', item.title);
          });
          
          setCompletedInstances(completedMap);
          console.log('Loaded completed instances:', Object.keys(completedMap).length);
        }
      } catch (error) {
        console.error('Error loading completed instances:', error);
      }
    };
    
    loadCompletedInstances();
    
    // Listen for task completion events to reload
    const handleTaskCompleted = () => {
      console.log('Task completed, reloading completed instances for calendar');
      loadCompletedInstances();
    };
    
    window.addEventListener('taskCompleted', handleTaskCompleted);
    
    return () => {
      window.removeEventListener('taskCompleted', handleTaskCompleted);
    };
  }, [API]);

  // Load real data instead of sample data
  useEffect(() => {
    const loadData = async () => {
      try {
        // Load work orders from API
        const response = await fetch(`${API}/work-orders`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (response.ok) {
          const orders = await response.json();
          console.log('Loaded work orders from API:', orders.length, 'orders');
          
          // Separate repair and PM work orders
          const repairOrders = orders.filter(wo => wo.type === 'Repair');
          const pmOrders = orders.filter(wo => wo.type === 'PM');
          
          console.log('Repair work orders:', repairOrders.length);
          console.log('PM work orders:', pmOrders.length);
          
          setWorkOrders(orders);
          
          // Group PM work orders by frequency for maintenance tasks
          // Also transform snake_case to camelCase for compatibility
          const transformedPmOrders = pmOrders.map(wo => ({
            ...wo,
            departmentName: wo.department_name,
            machineName: wo.machine_name,
            dayOfWeek: wo.day_of_week,
            monthlyPeriod: wo.monthly_period,
            yearlyMonth: wo.yearly_month
          }));
          
          const grouped = {
            daily: transformedPmOrders.filter(wo => wo.frequency === 'Daily'),
            weekly: transformedPmOrders.filter(wo => wo.frequency === 'Weekly'),
            monthly: transformedPmOrders.filter(wo => wo.frequency === 'Monthly'),
            yearly: transformedPmOrders.filter(wo => wo.frequency === 'Yearly')
          };
          
          console.log('Grouped PM work orders:', {
            daily: grouped.daily.length,
            weekly: grouped.weekly.length,
            monthly: grouped.monthly.length,
            yearly: grouped.yearly.length
          });
          
          console.log('Sample weekly task:', grouped.weekly[0]);
          console.log('Sample monthly task:', grouped.monthly[0]);
          
          setMaintenanceTasks(grouped);
        } else {
          console.error('Failed to load work orders, status:', response.status);
        }
      } catch (error) {
        console.error('Failed to load calendar data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();

    // Listen for updates
    const handleWorkOrderUpdate = () => {
      console.log('CalendarView: Received workOrderUpdated event, reloading data');
      loadData();
    };

    window.addEventListener('workOrderUpdated', handleWorkOrderUpdate);

    return () => {
      window.removeEventListener('workOrderUpdated', handleWorkOrderUpdate);
    };
  }, [API]);

  // Helper function to format date as YYYY-MM-DD without timezone conversion
  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper function to generate dates for recurring maintenance tasks
  const generateRecurringDates = (task, currentMonth, currentYear) => {
    const dates = [];
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    
    console.log('Generating dates for task:', task.title, 'Frequency:', task.frequency, 'dayOfWeek:', task.dayOfWeek, 'monthlyPeriod:', task.monthlyPeriod);
    
    if (task.frequency === 'Daily') {
      // Generate dates for Monday-Friday in the current month
      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(currentYear, currentMonth, day);
        const dayOfWeek = date.getDay();
        // 1-5 = Monday-Friday (0 = Sunday, 6 = Saturday)
        if (dayOfWeek >= 1 && dayOfWeek <= 5) {
          dates.push(formatDateLocal(date));
        }
      }
    } else if (task.frequency === 'Weekly' && task.dayOfWeek) {
      // Generate dates for specific day of week in the current month
      const dayMap = {
        'Sunday': 0, 'Monday': 1, 'Tuesday': 2, 'Wednesday': 3,
        'Thursday': 4, 'Friday': 5, 'Saturday': 6
      };
      const targetDay = dayMap[task.dayOfWeek];
      
      console.log('Weekly task - target day:', task.dayOfWeek, 'mapped to:', targetDay);
      
      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(currentYear, currentMonth, day);
        if (date.getDay() === targetDay) {
          dates.push(formatDateLocal(date));
        }
      }
    } else if (task.frequency === 'Monthly' && task.monthlyPeriod) {
      // Generate date based on start/middle/end of month with specific rules
      let targetDay = 1;
      
      if (task.monthlyPeriod === 'start') {
        // 1st of month, move to Monday if weekend
        targetDay = 1;
        const firstDate = new Date(currentYear, currentMonth, 1);
        const firstDayOfWeek = firstDate.getDay();
        if (firstDayOfWeek === 0) targetDay = 2; // Sunday -> Monday
        else if (firstDayOfWeek === 6) targetDay = 3; // Saturday -> Monday
      } else if (task.monthlyPeriod === 'middle') {
        // 15th of month, move to Monday if weekend
        targetDay = 15;
        const fifteenthDate = new Date(currentYear, currentMonth, 15);
        const fifteenthDayOfWeek = fifteenthDate.getDay();
        if (fifteenthDayOfWeek === 0) targetDay = 16; // Sunday -> Monday
        else if (fifteenthDayOfWeek === 6) targetDay = 17; // Saturday -> Monday
      } else if (task.monthlyPeriod === 'end') {
        // Last day of month, move to Friday before if weekend
        targetDay = daysInMonth;
        const lastDate = new Date(currentYear, currentMonth, daysInMonth);
        const lastDayOfWeek = lastDate.getDay();
        if (lastDayOfWeek === 0) targetDay = daysInMonth - 2; // Sunday -> Friday before
        else if (lastDayOfWeek === 6) targetDay = daysInMonth - 1; // Saturday -> Friday before
      }
      
      console.log('Monthly task - period:', task.monthlyPeriod, 'target day:', targetDay);
      dates.push(formatDateLocal(new Date(currentYear, currentMonth, targetDay)));
    } else if (task.frequency === 'Yearly' && task.yearlyMonth) {
      // Generate date for yearly task
      const monthMap = {
        'January': 0, 'February': 1, 'March': 2, 'April': 3,
        'May': 4, 'June': 5, 'July': 6, 'August': 7,
        'September': 8, 'October': 9, 'November': 10, 'December': 11
      };
      
      const targetMonth = monthMap[task.yearlyMonth];
      
      // Show task if it matches the calendar's current month
      // This handles both current year and next year scenarios
      if (targetMonth === currentMonth) {
        // Show on 1st of the selected month, move to Monday if weekend
        let targetDay = 1;
        const firstDate = new Date(currentYear, currentMonth, 1);
        const firstDayOfWeek = firstDate.getDay();
        if (firstDayOfWeek === 0) targetDay = 2; // Sunday -> Monday
        else if (firstDayOfWeek === 6) targetDay = 3; // Saturday -> Monday
        
        console.log('Yearly task - month:', task.yearlyMonth, 'year:', currentYear, 'target day:', targetDay);
        dates.push(formatDateLocal(new Date(currentYear, currentMonth, targetDay)));
      }
    }
    
    console.log('Generated dates:', dates);
    return dates;
  };

  // Convert real data to calendar events format
  const calendarEvents = [
    // Work orders with scheduled dates
    ...workOrders
      .filter(wo => {
        // Check if work order has any date field
        const hasDate = wo.due_date || wo.scheduled_start || wo.scheduled_date;
        console.log('Work order:', wo.wo_id, 'has date?', hasDate, 'due_date:', wo.due_date, 'scheduled_start:', wo.scheduled_start, 'scheduled_date:', wo.scheduled_date);
        return hasDate;
      })
      .map(wo => {
        // For positioning on calendar, prioritize scheduled_start (when task should start)
        // This is more important than due_date for calendar display
        let dateValue = wo.scheduled_start || wo.due_date || wo.scheduled_date;
        
        // Parse and format the date to YYYY-MM-DD format
        let formattedDate;
        try {
          if (typeof dateValue === 'string') {
            // If it's already in YYYY-MM-DD format, use it
            if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
              formattedDate = dateValue;
            } else if (/^\d{4}-\d{2}-\d{2}T/.test(dateValue)) {
              // If it's datetime format (YYYY-MM-DDTHH:MM), extract just the date part
              formattedDate = dateValue.split('T')[0];
            } else {
              // Try parsing as ISO date or datetime
              const parsedDate = new Date(dateValue);
              // Use UTC date to avoid timezone issues
              const year = parsedDate.getUTCFullYear();
              const month = String(parsedDate.getUTCMonth() + 1).padStart(2, '0');
              const day = String(parsedDate.getUTCDate()).padStart(2, '0');
              formattedDate = `${year}-${month}-${day}`;
            }
          } else if (dateValue instanceof Date) {
            const year = dateValue.getFullYear();
            const month = String(dateValue.getMonth() + 1).padStart(2, '0');
            const day = String(dateValue.getDate()).padStart(2, '0');
            formattedDate = `${year}-${month}-${day}`;
          } else {
            const today = new Date();
            const year = today.getFullYear();
            const month = String(today.getMonth() + 1).padStart(2, '0');
            const day = String(today.getDate()).padStart(2, '0');
            formattedDate = `${year}-${month}-${day}`;
          }
        } catch (e) {
          console.error('Error formatting date for work order', wo.wo_id, ':', e);
          const today = new Date();
          const year = today.getFullYear();
          const month = String(today.getMonth() + 1).padStart(2, '0');
          const day = String(today.getDate()).padStart(2, '0');
          formattedDate = `${year}-${month}-${day}`;
        }
        
        console.log('Formatted date for', wo.wo_id, ':', formattedDate);
        
        return {
          ...wo, // Include all original work order fields
          id: wo.id,
          wo_id: wo.wo_id,
          title: wo.title,
          type: wo.type || 'repair',
          date: formattedDate,
          time: wo.scheduled_start ? new Date(wo.scheduled_start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '09:00',
          scheduledTime: wo.scheduled_start ? new Date(wo.scheduled_start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '09:00',
          department: wo.department_name || wo.department || 'N/A',
        department_name: wo.department_name || wo.department,
        machine: wo.machine_name || wo.machine || 'N/A',
        machine_name: wo.machine_name || wo.machine,
        status: wo.status || 'scheduled',
        priority: wo.priority || 'Medium',
        assignee: wo.assignee || 'Unassigned',
        assignee_name: wo.assignee_name || wo.assignee,
        assignees: wo.assignees || [],  // Multiple assignees
        assignee_names: wo.assignee_names || [],  // Multiple assignee names
        requested_by_name: wo.requested_by_name || wo.requested_by,
        description: wo.description || '',
        created_at: wo.created_at,
        checklist: wo.checklist || [],
        tags: wo.tags || []
        };
      })
      .filter(repairEvent => {
        // Filter repair work orders - remove those completed for specific dates
        if (repairEvent.type !== 'Repair' && repairEvent.type !== 'repair') {
          return true; // Keep non-repair events
        }
        
        // Check if this repair work order was completed on this specific date
        const workOrderId = repairEvent.id;
        const eventDate = repairEvent.date;
        const key = `${workOrderId}_${eventDate}`;
        const isCompleted = completedInstances[key];
        
        if (isCompleted) {
          console.log(`Filtering out completed repair event: ${repairEvent.title} on ${eventDate}`);
        }
        
        return !isCompleted;
      }),
    
    // Maintenance tasks - generate recurring dates based on schedule type
    ...(() => {
      const monthYear = currentDate;
      const month = monthYear.getMonth();
      const year = monthYear.getFullYear();
      
      const recurringEvents = [];
      
      // Process daily tasks
      maintenanceTasks.daily.forEach(task => {
        const taskWithFrequency = { ...task, frequency: task.frequency || 'Daily' };
        const dates = generateRecurringDates(taskWithFrequency, month, year);
        
        dates.forEach(dateStr => {
          const workOrderId = taskWithFrequency.id;  // Original work order ID
          const key = `${workOrderId}_${dateStr}`;
          
          // Skip if this specific date instance was completed
          if (!completedInstances[key]) {
            recurringEvents.push({
              ...taskWithFrequency,
              id: `maintenance-daily-${taskWithFrequency.id || Math.random()}-${dateStr}`,
              type: 'maintenance',
              frequency: 'Daily',
              date: dateStr,
              time: taskWithFrequency.time || '08:00',
              scheduledTime: taskWithFrequency.time || '08:00',
              department: taskWithFrequency.departmentName || taskWithFrequency.department || 'N/A',
              machine: taskWithFrequency.machineName || taskWithFrequency.machine || 'N/A',
              status: taskWithFrequency.status || 'scheduled',
              priority: taskWithFrequency.priority || 'Medium'
            });
          }
        });
      });
      
      // Process weekly tasks
      maintenanceTasks.weekly.forEach(task => {
        const taskWithFrequency = { ...task, frequency: task.frequency || 'Weekly' };
        const dates = generateRecurringDates(taskWithFrequency, month, year);
        
        dates.forEach(dateStr => {
          const workOrderId = taskWithFrequency.id;
          const key = `${workOrderId}_${dateStr}`;
          
          if (!completedInstances[key]) {
            recurringEvents.push({
              ...taskWithFrequency,
              id: `maintenance-weekly-${taskWithFrequency.id || Math.random()}-${dateStr}`,
              type: 'maintenance',
              frequency: 'Weekly',
              date: dateStr,
              time: taskWithFrequency.time || '08:00',
              scheduledTime: taskWithFrequency.time || '08:00',
              department: taskWithFrequency.departmentName || taskWithFrequency.department || 'N/A',
              machine: taskWithFrequency.machineName || taskWithFrequency.machine || 'N/A',
              status: taskWithFrequency.status || 'scheduled',
              priority: taskWithFrequency.priority || 'Medium',
              dayOfWeek: taskWithFrequency.dayOfWeek
            });
          }
        });
      });
      
      // Process monthly tasks
      maintenanceTasks.monthly.forEach(task => {
        const taskWithFrequency = { ...task, frequency: task.frequency || 'Monthly' };
        const dates = generateRecurringDates(taskWithFrequency, month, year);
        
        dates.forEach(dateStr => {
          const workOrderId = taskWithFrequency.id;
          const key = `${workOrderId}_${dateStr}`;
          
          if (!completedInstances[key]) {
            recurringEvents.push({
              ...taskWithFrequency,
              id: `maintenance-monthly-${taskWithFrequency.id || Math.random()}-${dateStr}`,
              type: 'maintenance',
              frequency: 'Monthly',
              date: dateStr,
              time: taskWithFrequency.time || '08:00',
              scheduledTime: taskWithFrequency.time || '08:00',
              department: taskWithFrequency.departmentName || taskWithFrequency.department || 'N/A',
              machine: taskWithFrequency.machineName || taskWithFrequency.machine || 'N/A',
              status: taskWithFrequency.status || 'scheduled',
              priority: taskWithFrequency.priority || 'Medium',
              monthlyPeriod: taskWithFrequency.monthlyPeriod
            });
          }
        });
      });
      
      // Process yearly tasks
      maintenanceTasks.yearly.forEach(task => {
        const taskWithFrequency = { ...task, frequency: task.frequency || 'Yearly' };
        const dates = generateRecurringDates(taskWithFrequency, month, year);
        
        dates.forEach(dateStr => {
          const workOrderId = taskWithFrequency.id;
          const key = `${workOrderId}_${dateStr}`;
          
          if (!completedInstances[key]) {
            recurringEvents.push({
              ...taskWithFrequency,
              id: `maintenance-yearly-${taskWithFrequency.id || Math.random()}-${dateStr}`,
              type: 'maintenance',
              frequency: 'Yearly',
              date: dateStr,
              time: taskWithFrequency.time || '08:00',
              scheduledTime: taskWithFrequency.time || '08:00',
              department: taskWithFrequency.departmentName || taskWithFrequency.department || 'N/A',
              machine: taskWithFrequency.machineName || taskWithFrequency.machine || 'N/A',
              status: taskWithFrequency.status || 'scheduled',
              priority: taskWithFrequency.priority || 'Medium',
              yearlyMonth: taskWithFrequency.yearlyMonth
            });
          }
        });
      });
      
      console.log('Generated recurring maintenance events (after filtering completed):', recurringEvents.length);
      
      return recurringEvents;
    })()
  ];

  console.log('Total calendar events:', calendarEvents.length);
  console.log('Calendar events:', calendarEvents);
  const repairEvents = calendarEvents.filter(e => e.type === 'repair' || e.type === 'corrective');
  const maintenanceEvents = calendarEvents.filter(e => e.type === 'maintenance');
  console.log('Repair events:', repairEvents.length);
  console.log('Maintenance events:', maintenanceEvents.length);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const getEventsForDate = (date) => {
    if (!date) return [];
    // Use local date components (same as event date strings) to avoid UTC off-by-one
    const dateString = formatDateLocal(date);
    return calendarEvents.filter(event => event.date === dateString);
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isSelected = (date) => {
    if (!date) return false;
    return date.toDateString() === selectedDate.toDateString();
  };

  const getEventTypeIcon = (type) => {
    return type === 'maintenance' ? Settings : Wrench;
  };

  const getEventTypeColor = (type, frequency) => {
    // For repair work orders
    if (type === 'repair' || type === 'corrective') {
      return 'bg-red-100 text-red-800 border-red-200';
    }
    
    // For maintenance tasks, color code by frequency
    if (type === 'maintenance') {
      if (frequency === 'Daily') {
        return 'bg-blue-100 text-blue-800 border-blue-200';
      } else if (frequency === 'Weekly') {
        return 'bg-green-100 text-green-800 border-green-200';
      } else if (frequency === 'Monthly') {
        return 'bg-purple-100 text-purple-800 border-purple-200';
      } else if (frequency === 'Yearly') {
        return 'bg-orange-100 text-orange-800 border-orange-200';
      }
      // Default maintenance color
      return 'bg-green-100 text-green-800 border-green-200';
    }
    
    // Default fallback
    return 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getStatusColor = (status) => {
    const colors = {
      'scheduled': 'bg-blue-500',
      'in-progress': 'bg-yellow-500',
      'completed': 'bg-green-500',
      'on-hold': 'bg-red-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  const handleTaskClick = (task) => {
    setSelectedTask(task);
  };

  const handleTaskUpdate = (updatedTask) => {
    // In a full implementation, you'd update the task in the state
    setSelectedTask(null);
  };

  const monthNames = t('months');
  const dayNames = t('weekDaysShort');

  const days = getDaysInMonth(currentDate);
  const selectedDateEvents = getEventsForDate(selectedDate);

  return (
    <div className="space-y-6" data-testid="calendar-view">
      {/* Header */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 flex items-center">
              <Calendar className="w-6 h-6 mr-2 text-blue-600" />
              {t('calendarTitle')}
            </h2>
            <p className="text-gray-600 mt-1">
              {t('viewScheduledMaintenance')}
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSelectedDate(new Date())}
            >
              {t('today')}
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <Card className="lg:col-span-2 border-0 shadow-sm">
          <CardHeader className="border-b">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigateMonth(-1)}
                  className="h-8 w-8 p-0"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <CardTitle className="text-xl">
                  {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigateMonth(1)}
                  className="h-8 w-8 p-0"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-4">
            {/* Day headers */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {dayNames.map(day => (
                <div key={day} className="p-2 text-center text-sm font-medium text-gray-600">
                  {day}
                </div>
              ))}
            </div>
            
            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-1">
              {days.map((day, index) => {
                const events = getEventsForDate(day);
                const hasEvents = events.length > 0;
                
                return (
                  <div
                    key={index}
                    className={`
                      min-h-24 p-1 border border-gray-100 cursor-pointer transition-colors
                      ${day ? 'hover:bg-gray-50' : 'bg-gray-25'}
                      ${isToday(day) ? 'bg-blue-50 border-blue-200' : ''}
                      ${isSelected(day) ? 'ring-2 ring-blue-500 bg-blue-25' : ''}
                    `}
                    onClick={() => day && setSelectedDate(day)}
                  >
                    {day && (
                      <>
                        <div className={`
                          text-sm font-medium mb-1
                          ${isToday(day) ? 'text-blue-600' : 'text-gray-900'}
                        `}>
                          {day.getDate()}
                        </div>
                        
                        {hasEvents && (
                          <div className="space-y-1">
                            {events.slice(0, 2).map(event => {
                              const Icon = getEventTypeIcon(event.type);
                              return (
                                <div
                                  key={event.id}
                                  className={`
                                    text-xs p-1 rounded border
                                    ${getEventTypeColor(event.type, event.frequency)}
                                    flex items-center space-x-1
                                  `}
                                >
                                  <div className={`w-2 h-2 rounded-full ${getStatusColor(event.status)}`}></div>
                                  <Icon className="w-3 h-3" />
                                  <span className="truncate flex-1">{event.title}</span>
                                </div>
                              );
                            })}
                            {events.length > 2 && (
                              <div className="text-xs text-gray-500 px-1">
                                +{events.length - 2} more
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Selected Date Details */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="border-b">
            <CardTitle className="text-lg">
              {selectedDate.toLocaleDateString('en-US', { 
                weekday: 'long',
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            {selectedDateEvents.length > 0 ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-medium text-gray-600">
                    {selectedDateEvents.length} task{selectedDateEvents.length !== 1 ? 's' : ''} scheduled
                  </span>
                </div>
                
                {selectedDateEvents.map(event => {
                  const Icon = getEventTypeIcon(event.type);
                  return (
                    <div 
                      key={event.id} 
                      className="p-3 bg-gray-50 rounded-lg border cursor-pointer hover:bg-gray-100 transition-colors duration-200"
                      onClick={() => handleTaskClick(event)}
                    >
                      <div className="flex items-start space-x-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                          event.type === 'maintenance' ? 'bg-green-100' : 'bg-blue-100'
                        }`}>
                          <Icon className={`w-4 h-4 ${
                            event.type === 'maintenance' ? 'text-green-600' : 'text-blue-600'
                          }`} />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900 text-sm">{event.title}</h4>
                          <div className="text-xs text-gray-600 mt-1 space-y-1">
                            <div className="flex items-center">
                              <Clock className="w-3 h-3 mr-1" />
                              {event.time}
                            </div>
                            <div>{event.department} • {event.machine}</div>
                          </div>
                          <div className="mt-2">
                            <Badge className={`${getEventTypeColor(event.type, event.frequency)} text-xs`}>
                              {event.type}
                            </Badge>
                            <div className={`inline-block w-2 h-2 rounded-full ml-2 ${getStatusColor(event.status)}`}></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="text-sm">{t('noTasksScheduled')}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Task Detail Modal */}
      {selectedTask && (
        <>
          {selectedTask.type === 'maintenance' ? (
            <MaintenanceTaskDetail
              task={selectedTask}
              onClose={() => setSelectedTask(null)}
              onUpdate={handleTaskUpdate}
              user={user}
              readOnly={true}
            />
          ) : (
            <WorkOrderDetail
              workOrder={selectedTask}
              onClose={() => setSelectedTask(null)}
              onUpdate={handleTaskUpdate}
              user={user}
              readOnly={true}
            />
          )}
        </>
      )}
    </div>
  );
}

export default CalendarView;